import railsFetch from "../railsFetch";
import { toastManager } from "./ToastManager";
import { ReportExport } from "./types";

export interface DownloadFlowCallbacks {
  onConfirm?: (exportType: string) => void;
}

export interface SelectionParams {
  selection_mode: "explicit" | "all_matching";
  record_ids?: number[];
  excluded_ids?: number[];
  filter_params?: Record<string, unknown>;
}

// Track active polls by exportId to prevent duplicate polling/toasts
const activePolls = new Map<number, () => void>();

// Helper function to start an export and trigger notifications
export const startExport = async (
  reportExportsUrl: string,
  gridType: string,
  exportType: "csv" | "xlsx",
  params: Record<string, unknown>,
  reportId?: number,
  callbacks?: DownloadFlowCallbacks,
  selection?: SelectionParams
): Promise<{ id: number } | null> => {
  try {
    const response = await railsFetch(reportExportsUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        report_export: {
          export_type: exportType,
          grid_type: gridType,
          report_id: reportId,
          params,
          ...(selection && {
            selection_mode: selection.selection_mode,
            record_ids: selection.record_ids,
            excluded_ids: selection.excluded_ids,
            filter_params: selection.filter_params,
          }),
        },
      }),
    });

    if (response.ok) {
      const data = await response.json();
      
      // Determine if this is an existing export or a new one
      // Backend returns 202 (accepted) with message "Export already in progress" for existing exports
      const isExisting = response.status === 202 && data.message?.includes("already");
      
      if (isExisting) {
        // For existing exports, show a toast notification instead of dialog
        const statusText = data.status === "pending" ? "queued" : "processing";
        const icon = data.status === "pending" ? "blocked" : "sync";
        toastManager.show({
          message: `Download already ${statusText}`,
          subtitle: "You'll be notified when it's ready.",
          variant: "warning",
          icon,
          autoCloseMs: 5000,
        });
      } else {
        // For new exports, show confirmation dialog
        if (callbacks?.onConfirm) {
          callbacks.onConfirm(exportType);
        }
      }
      
      // Dispatch custom event to notify the download tray
      window.dispatchEvent(
        new CustomEvent("reportExportStarted", {
          detail: { exportId: data.id, exportType },
        })
      );
      return data;
    } else {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData.errors?.join(", ") || "Failed to start export";
      console.error("[startExport] Failed to start export:", errorMessage);
      return null;
    }
  } catch (error) {
    console.error("[startExport] Failed to start export:", error);
    return null;
  }
};

// Poll for export completion and show toast
// Returns a cleanup function to cancel polling if needed
export const pollExportStatus = (
  reportExportsUrl: string,
  exportId: number,
  exportType: string,
  gridType: string
): (() => void) => {
  const existingPoll = activePolls.get(exportId);
  if (existingPoll) {
    return existingPoll;
  }

  const maxAttempts = 60; // 3 minutes max (60 * 3 seconds)
  let attempts = 0;
  let cancelled = false;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let initialTimeoutId: ReturnType<typeof setTimeout> | null = null;

  const poll = async (): Promise<void> => {
    if (cancelled) return;

    if (attempts >= maxAttempts) {
      console.warn("[pollExportStatus] Polling timeout reached");
      activePolls.delete(exportId);
      return;
    }

    try {
      const response = await railsFetch(
        `${reportExportsUrl}/${exportId}.json`,
        { method: "GET" }
      );

      if (cancelled) return;

      if (response.ok) {
        const exportData: ReportExport = await response.json();

        if (exportData.status === "complete" && exportData.downloadUrl) {
          // Show toast notification
          const name = exportData.reportName || gridType;
          toastManager.show({
            message: `${name} ${exportType.toUpperCase()} export is ready`,
            downloadUrl: exportData.downloadUrl,
            expiresAt: exportData.expiresAt,
            autoCloseMs: 8000,
          });

          // Dispatch event to notify download tray
          window.dispatchEvent(
            new CustomEvent("downloadComplete", {
              detail: { exportId, exportData },
            })
          );
          activePolls.delete(exportId);
          return;
        } else if (exportData.status === "failed") {
          toastManager.show({
            message: `Export failed: ${exportData.errorMessage || "Unknown error"}`,
            variant: "danger",
            autoCloseMs: 5000,
          });
          activePolls.delete(exportId);
          return;
        }

        // Still processing, poll again
        if (!cancelled) {
          attempts++;
          timeoutId = setTimeout(poll, 3000);
        }
      } else {
        // Non-OK response - continue polling (might be transient error)
        // but increment attempts to respect maxAttempts limit
        if (!cancelled) {
          attempts++;
          timeoutId = setTimeout(poll, 3000);
        }
      }
    } catch (error) {
      console.error("[pollExportStatus] Error polling export status:", error);
      // Continue polling on network errors (might be transient)
      if (!cancelled && attempts < maxAttempts) {
        attempts++;
        timeoutId = setTimeout(poll, 3000);
      }
    }
  };

  // Start polling after a short delay
  initialTimeoutId = setTimeout(poll, 1000);

  // Return cleanup function and register it
  const cleanup = () => {
    cancelled = true;
    if (timeoutId) clearTimeout(timeoutId);
    if (initialTimeoutId) clearTimeout(initialTimeoutId);
    activePolls.delete(exportId);
  };

  activePolls.set(exportId, cleanup);

  return cleanup;
};

